import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@shared/services/api';
import { Button, Card } from '@shared/components/ui';
import {
  Settings,
  Lock,
  CheckCircle2,
  AlertCircle,
  Database,
  Mail,
  Shield,
  Zap,
  MapPin,
} from 'lucide-react';

interface SystemSettingValue {
  value: string;
  type: string;
}

interface SystemSetting {
  id: string;
  key: string;
  value: SystemSettingValue;
  description: string;
  category: string;
  updated_at: string;
  updated_by?: string;
}

interface SettingsResponse {
  settings: Record<string, SystemSetting[]>;
  categories: string[];
}

export default function AdminSettings() {
  const { t, i18n } = useTranslation('admin');
  const queryClient = useQueryClient();
  const [selectedCategory, setSelectedCategory] = useState('smtp');
  const [successMessage, setSuccessMessage] = useState('');

  const {
    data: settingsData,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['admin', 'settings'],
    queryFn: async () => {
      const response = await api.get('/admin/settings');
      return response.data as SettingsResponse;
    },
    enabled: true,
  });

  const updateMutation = useMutation({
    mutationFn: async ({ key, value }: { key: string; value: any }) => {
      return api.post('/admin/settings/update', {
        key,
        value: JSON.stringify(value),
        reason: 'Updated via admin panel',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'settings'] });
      setSuccessMessage(t('settings.success'));
      setTimeout(() => setSuccessMessage(''), 3000);
    },
  });

  const handleUpdate = (key: string, value: string) => {
    const currentSettings = settingsData?.settings[selectedCategory] || [];
    const setting = currentSettings.find((s) => s.key === key);
    if (!setting) return;

    const updateValue = {
      value,
      type: setting.value.type,
    };

    updateMutation.mutate({ key, value: updateValue });
  };

  const getCategoryIcon = (category: string) => {
    switch (category.toLowerCase()) {
      case 'smtp':
        return <Mail size={18} />;
      case 'database':
        return <Database size={18} />;
      case 'security':
        return <Shield size={18} />;
      case 'performance':
        return <Zap size={18} />;
      case 'data_locations':
        return <MapPin size={18} />;
      default:
        return <Settings size={18} />;
    }
  };

  const currentSettings = settingsData?.settings[selectedCategory] || [];

  return (
    <>
      <div className="space-y-8 animate-in fade-in duration-700">
        <div>
          <h1 className="app-page-title">{t('settings.title')}</h1>
          <p className="text-text-muted-light dark:text-text-muted-dark font-medium mt-1">
            {t('settings.subtitle')}
          </p>
        </div>

        {successMessage && (
          <div className="bg-state-ok-bg-light dark:bg-state-ok-bg-dark border border-state-ok-light/20 dark:border-state-ok-dark/20 p-4 rounded-xl flex items-center gap-3 text-state-ok-light dark:text-state-ok-dark font-bold animate-in slide-in-from-top duration-300">
            <CheckCircle2 size={20} className="text-state-ok-light dark:text-state-ok-dark" />
            {successMessage}
          </div>
        )}

        {/* Category Navigation */}
        <div className="grid grid-cols-2 gap-2 lg:grid-cols-5">
          {settingsData?.categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              aria-pressed={selectedCategory === category}
              className={`flex min-h-11 w-full items-center justify-center gap-2 rounded-xl px-3 py-2 text-center font-bold text-sm leading-tight transition-all ${
                selectedCategory === category
                  ? 'bg-brand-600 text-white shadow-brand-200'
                  : 'bg-surface-card-light dark:bg-surface-card-dark text-slate-600 dark:text-slate-300 border border-surface-border-light dark:border-surface-border-dark hover:border-brand-200'
              }`}
            >
              {getCategoryIcon(category)}
              <span>{translateCategory(category, t)}</span>
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            {isLoading ? (
              <div className="py-20 flex justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
              </div>
            ) : isError && !settingsData ? (
              <div role="alert">
                <Card className="flex flex-col items-start gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-text-primary-light dark:text-text-primary-dark">
                    {t('settings.loadFailed')}
                  </p>
                  <Button
                    type="button"
                    variant="secondary"
                    className="min-h-11"
                    onClick={() => void refetch()}
                  >
                    {t('settings.retry')}
                  </Button>
                </Card>
              </div>
            ) : (
              <Card className="divide-y divide-surface-border-light dark:divide-surface-border-dark">
                <div className="p-6 bg-surface-2-light dark:bg-surface-2-dark">
                  <h3 className="text-lg font-black text-slate-900 dark:text-white flex items-center gap-2">
                    {getCategoryIcon(selectedCategory)}{' '}
                    {selectedCategory === 'general'
                      ? t('settings.generalConfiguration')
                      : t('settings.configuration', {
                          category: formatConfigurationCategory(
                            selectedCategory,
                            t,
                            i18n.resolvedLanguage || i18n.language
                          ),
                        })}
                  </h3>
                </div>
                <div className="p-6 space-y-8">
                  {currentSettings.map((setting) => {
                    const fieldId = `admin-setting-${normalizeSettingKey(setting.key)}`;
                    return (
                      <div key={setting.key} className="space-y-3 group">
                        <div>
                          <label
                            htmlFor={fieldId}
                            className="text-xs font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark"
                          >
                            {translateSetting(setting.key, setting.description, t)}
                          </label>
                        </div>

                        <div className="flex gap-3">
                          {setting.value.type === 'boolean' ? (
                            <select
                              id={fieldId}
                              value={setting.value.value || 'false'}
                              onChange={(e) => handleUpdate(setting.key, e.target.value)}
                              className="flex-1 px-4 py-3 bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl text-sm font-bold focus:ring-2 focus:ring-brand-500 outline-none appearance-none cursor-pointer"
                            >
                              <option value="true">{t('settings.enabled')}</option>
                              <option value="false">{t('settings.disabled')}</option>
                            </select>
                          ) : setting.value.type === 'password' ? (
                            <div className="relative flex-1">
                              <input
                                id={fieldId}
                                type="password"
                                defaultValue={setting.value.value || ''}
                                onBlur={(e) => {
                                  if (e.target.value !== setting.value.value) {
                                    handleUpdate(setting.key, e.target.value);
                                  }
                                }}
                                className="w-full pl-10 pr-4 py-3 bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl text-sm font-bold focus:ring-2 focus:ring-brand-500 outline-none"
                              />
                              <Lock
                                size={16}
                                className="absolute left-3.5 top-3.5 text-text-subtle-light dark:text-text-subtle-dark"
                              />
                            </div>
                          ) : (
                            <input
                              id={fieldId}
                              type="text"
                              defaultValue={setting.value.value || ''}
                              onBlur={(e) => {
                                if (e.target.value !== setting.value.value) {
                                  handleUpdate(setting.key, e.target.value);
                                }
                              }}
                              className="flex-1 px-4 py-3 bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl text-sm font-bold focus:ring-2 focus:ring-brand-500 outline-none"
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {currentSettings.length === 0 && (
                    <p className="text-center py-12 text-text-subtle-light dark:text-text-subtle-dark font-medium">
                      {t('settings.noSettings')}
                    </p>
                  )}
                </div>
              </Card>
            )}
          </div>

          <div className="space-y-6">
            <Card className="p-6 border-l-4 border-l-brand-500">
              <h4 className="text-sm font-black text-slate-900 dark:text-white mb-3 flex items-center gap-2">
                <Shield className="text-brand-600 dark:text-brand-400" size={16} />{' '}
                {t('settings.controls.title')}
              </h4>
              <p className="text-xs text-text-muted-light dark:text-text-muted-dark font-medium leading-relaxed mb-4">
                {t('settings.controls.description')}
              </p>
              {updateMutation.isPending && (
                <div className="flex items-center gap-2 text-brand-600 dark:text-brand-400 font-bold text-xs animate-pulse">
                  <div className="h-2 w-2 rounded-full bg-brand-600"></div>
                  {t('settings.controls.saving')}
                </div>
              )}
              {updateMutation.isError && (
                <div className="flex items-center gap-2 text-state-danger-light dark:text-state-danger-dark font-bold text-xs">
                  <AlertCircle size={14} /> {t('settings.controls.failed')}
                </div>
              )}
            </Card>

            <Card className="p-6 bg-surface-dark text-white">
              <h4 className="text-sm font-black uppercase tracking-widest text-text-subtle-dark mb-4">
                {t('settings.platformInfo.title')}
              </h4>
              <div className="space-y-4">
                <div>
                  <p className="text-[10px] font-black uppercase tracking-tighter text-text-muted-dark">
                    {t('settings.platformInfo.nodeVersion')}
                  </p>
                  <p className="text-sm font-bold font-mono">v18.17.1</p>
                </div>
                <div>
                  <p className="text-[10px] font-black uppercase tracking-tighter text-text-muted-dark">
                    {t('settings.platformInfo.apiEndpoint')}
                  </p>
                  <p className="text-sm font-bold font-mono">/api/v1</p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
}

function normalizeSettingKey(value: string) {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
}

function translateCategory(category: string, t: (key: string) => string) {
  const key = `settings.categories.${normalizeSettingKey(category)}`;
  const translated = t(key);
  return translated === key
    ? category.replace(/[_]+/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
    : translated;
}

function formatConfigurationCategory(
  category: string,
  t: (key: string) => string,
  language: string
) {
  const translated = translateCategory(category, t);
  if (language.toLowerCase().startsWith('en') || /^[A-Z0-9 ._-]+$/.test(translated)) {
    return translated;
  }

  return translated.toLocaleLowerCase(language);
}

function translateSetting(keyName: string, description: string, t: (key: string) => string) {
  const key = `settings.settingDescriptions.${normalizeSettingKey(keyName)}`;
  const translated = t(key);
  return translated === key ? description : translated;
}
